import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider, MutationCache, QueryCache } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";
import { initSentry } from "./lib/sentry";
import { assertRequiredEnv } from "./lib/envCheck";
import { checkAndUpdateBuildVersion } from "./utils/buildVersionCheck";
import { initPWA, cleanupServiceWorkersInDev } from "./lib/pwa";
import { CACHE_TIMES } from "./constants/time";
import { shouldEnablePWA, isPreviewHost } from "./lib/previewDetection";

import { logger } from '@/lib/logger';
// Inicializa Sentry para monitoramento de erros e performance
initSentry();

// 🔒 Fail-fast: env vars críticas (Supabase) precisam existir antes de qualquer fetch
assertRequiredEnv();

// 🔄 PWA: Em produção (não-preview) inicializa e verifica versão
// Em dev ou preview, limpa resíduos para evitar instabilidade
if (shouldEnablePWA()) {
  checkAndUpdateBuildVersion();
  initPWA();
} else {
  // Limpa Service Workers residuais em dev OU preview
  // No preview, isso evita caching problemático
  cleanupServiceWorkersInDev().catch(() => {});
  
  // Log para debug em preview
  if (isPreviewHost()) {
    logger.info('🔧 Preview mode: PWA e build version check desativados');
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: CACHE_TIMES.DEFAULT.staleTime,
      gcTime: CACHE_TIMES.DEFAULT.gcTime,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
  // Captura global: erros silenciosos de queries/mutations passam a aparecer no Sentry
  queryCache: new QueryCache({
    onError: (error, query) => {
      import('@/lib/sentry').then(({ captureError }) => {
        captureError(error, {
          tags: { source: 'react-query', kind: 'query' },
          extra: { queryKey: JSON.stringify(query.queryKey) },
        });
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      import('@/lib/sentry').then(({ captureError }) => {
        captureError(error, {
          tags: { source: 'react-query', kind: 'mutation' },
          extra: { mutationKey: JSON.stringify(mutation.options.mutationKey ?? []) },
        });
      });
    },
  }),
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        {/* 🚀 FASE 0: Zustand não precisa de Providers! */}
        <App />
      </QueryClientProvider>
    </HelmetProvider>
  </StrictMode>
);
